import ReactDOM from 'react-dom';

type HydrationRoot = {
	unstable_scheduleHydration(node: Node): void;
};

/**
 * Find all resolved suspense boundaries in the root node
 *
 * A resolved hydration boundary is denoted by a pair of comment nodes, the first has
 * the value "$" and the second has the value "/$"
 *
 * An unresolved hydration boundary with fallback will instead have a "$?" at the start
 *
 * @param rootNode - The root node to search for suspense boundaries
 * @returns An array of all suspense boundaries found in the root node
 */
export function findAllResolvedSuspenseBoundaries(rootNode: Node) {
	const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_COMMENT, {
		acceptNode: (node) => {
			return node.nodeValue === '$' ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
		},
	});

	const suspenseComments: Node[] = [];
	let currentNode: Node | null = null;
	while ((currentNode = walker.nextNode())) {
		suspenseComments.push(currentNode);
	}

	return suspenseComments;
}

/**
 * Find the first hydratable node after a resolved suspense boundary - it is possible
 * to schedule a hydration synchronously for any node within a suspense boundary
 *
 * @param boundary - The resolved suspense boundary to find the first hydratable node after
 * @returns The first hydratable node after the resolved suspense boundary
 */
export function findFirstHydratableNode(boundary: Node) {
	let nextNode = boundary.nextSibling;

	// Skip text nodes that are just whitespace
	while (nextNode && nextNode.nodeType === Node.TEXT_NODE && !nextNode.textContent?.trim()) {
		nextNode = nextNode.nextSibling;
	}

	// Return the next node if it is an element node
	if (nextNode && nextNode.nodeType === Node.ELEMENT_NODE) {
		return nextNode;
	}
}

/**
 * Schedule a synchronous hydration for all resolved suspense boundaries in the root node
 *
 * This is a workaround for an issue in React 18 where hydration can force multiple render / discard cycles
 *
 * https://hello.atlassian.net/wiki/spaces/~moates/pages/5497372057/Challenges+with+Selective+Hydration
 *
 * When we move to React 19 we can remove this.
 *
 * @param rootNode - The root node to schedule synchronous hydration for
 * @param root - The root node to schedule synchronous hydration for
 */
export function scheduleSyncHydration(rootNode: Node, root: HydrationRoot) {
	ReactDOM.flushSync(() => {
		findAllResolvedSuspenseBoundaries(rootNode).forEach((boundary) => {
			const node = findFirstHydratableNode(boundary);

			if (node) {
				root.unstable_scheduleHydration(node);
			}
		});
		root.unstable_scheduleHydration(rootNode);
	});
}
